<section class="profile-summary" aria-labelledby="profile-summary-title">
  @if (showBackButton()) {
    <button
      type="button"
      class="profile-summary__back"
      aria-label="Back to pet parents"
      (click)="back.emit()"
    >
      <lib-icon
        class="profile-summary__back-icon"
        iconName="backIcon"
        aria-hidden="true"
      ></lib-icon>

      <span class="profile-summary__back-text"> Back </span>
    </button>
  }

  <header class="profile-summary__header">
    <div class="profile-summary__identity">
      <lib-icon
        class="profile-summary__avatar"
        iconName="adopterProfileIcon"
        aria-hidden="true"
      ></lib-icon>

      <h1 id="profile-summary-title" class="profile-summary__title">
        {{ adopter().firstName }}
        {{ adopter().lastName }}
      </h1>
    </div>

    @if (showProfileEdit()) {
      <button
        type="button"
        class="profile-summary__edit"
        aria-label="Edit adopter profile"
        title="Edit adopter profile"
        (click)="editProfile.emit()"
      >
        <lib-icon
          class="profile-summary__edit-icon"
          iconName="editIcon"
          aria-hidden="true"
        ></lib-icon>

        <span class="profile-summary__edit-text"> Edit </span>
      </button>
    }
  </header>

  <div class="profile-summary__notice">
    <ng-content select="[profile-summary-notice]" />
  </div>

  <div class="profile-summary__columns">
    <dl class="profile-summary__details">
      @if (showPetszelId()) {
        <div class="profile-summary__field">
          <dt>Petszel ID</dt>

          <dd>
            {{ adopter().id || "N/A" }}
          </dd>
        </div>
      }

      @if (adopter().personId; as personId) {
        <div class="profile-summary__field">
          <dt>Person ID</dt>

          <dd>
            <lib-linkout
              [recordId]="personId"
              recordLabel="Person ID"
              [externalUrl]="personIdUrl()"
              [allowCopy]="allowPersonIdCopy()"
              [showShelterluvHelp]="showShelterluvLinkoutHelp()"
              [isAdopterUnknown]="isAdopterUnknown()"
            />
          </dd>
        </div>
      }

      <div class="profile-summary__field">
        <dt>Name</dt>

        <dd>
          {{ adopter().firstName }}
          {{ adopter().lastName }}
        </dd>
      </div>

      <div class="profile-summary__field">
        <dt>Species</dt>

        <dd>Human</dd>
      </div>

      <div class="profile-summary__field profile-summary__field--top">
        <dt>Address</dt>

        <dd>
          {{ adopter().address || "N/A" }}
        </dd>
      </div>
    </dl>

    <dl class="profile-summary__details">
      <div class="profile-summary__field profile-summary__field--top">
        <dt>Email</dt>

        <dd class="profile-summary__contact-cell">
          <div class="profile-summary__contact">
            <span class="profile-summary__contact-value">
              @if (adopter().email; as email) {
                <a [href]="'mailto:' + email">
                  {{ email }}
                </a>
              } @else {
                <span class="profile-summary__empty"> N/A </span>
              }
            </span>

            @if (allowEmailEditing()) {
              <button
                type="button"
                class="petszel-primary-button"
                (click)="adopter().email ? editEmail.emit() : addEmail.emit()"
              >
                {{ adopter().email ? "Edit" : "Add email" }}
              </button>
            }
          </div>

          @if (subscriptions("Email"); as emailSubscriptions) {
            @if (emailSubscriptions.length) {
              <ul class="profile-summary__subscriptions">
                @for (
                  subscription of emailSubscriptions;
                  track subscription.messageId
                ) {
                  <li class="profile-summary__subscription">
                    {{ subscriptionLabel(subscription.name) }}

                    <span class="profile-summary__subscription-id">
                      (ID: {{ subscription.messageId }})
                    </span>

                    <span
                      class="
                        profile-summary__status
                        {{ statusClass(subscription.allowed) }}
                      "
                    >
                      ({{ statusLabel(subscription.allowed) }})
                    </span>
                  </li>
                }
              </ul>
            }
          }
        </dd>
      </div>

      <div class="profile-summary__field profile-summary__field--top">
        <dt>Phone</dt>

        <dd class="profile-summary__contact-cell">
          <div class="profile-summary__contact">
            <span class="profile-summary__contact-value">
              @if (adopter().phone; as phone) {
                <a [href]="'tel:' + phone">
                  {{ phone }}
                </a>
              } @else {
                <span class="profile-summary__empty"> N/A </span>
              }
            </span>

            @if (allowPhoneEditing()) {
              <button
                type="button"
                class="petszel-primary-button"
                (click)="editPhone.emit()"
              >
                Edit
              </button>
            } @else {
              <ng-content select="[phone-action]" />
            }
          </div>

          @if (subscriptions("Text"); as textSubscriptions) {
            @if (textSubscriptions.length) {
              <ul class="profile-summary__subscriptions">
                @for (
                  subscription of textSubscriptions;
                  track subscription.messageId
                ) {
                  <li class="profile-summary__subscription">
                    {{ subscriptionLabel(subscription.name) }}

                    <span class="profile-summary__subscription-id">
                      (ID: {{ subscription.messageId }})
                    </span>

                    <span
                      class="
                        profile-summary__status
                        {{ statusClass(subscription.allowed) }}
                      "
                    >
                      ({{ statusLabel(subscription.allowed) }})
                    </span>
                  </li>
                }
              </ul>
            }
          }
        </dd>
      </div>

      @if (adopter().preferredContact; as preferredContact) {
        <div class="profile-summary__field">
          <dt>Preferred contact</dt>

          <dd>
            {{ preferredContact }}
          </dd>
        </div>
      }

      @if (adopter().preferredLanguage; as preferredLanguage) {
        <div class="profile-summary__field">
          <dt>Preferred language</dt>

          <dd>
            {{ preferredLanguage }}
          </dd>
        </div>
      }

      @if (showOperationalDetails()) {
        <div class="profile-summary__field">
          <dt>Last login date</dt>

          <dd>
            {{ adopter().lastLoginDate || "N/A" }}
          </dd>
        </div>

        <div class="profile-summary__field">
          <dt>Login count</dt>

          <dd>
            {{ adopter().loginCount ?? "N/A" }}
          </dd>
        </div>

        @if (allowImpersonation()) {
          <div class="profile-summary__field">
            <dt>Impersonate</dt>

            <dd>
              <button
                type="button"
                class="petszel-primary-button"
                (click)="impersonate.emit()"
              >
                Impersonate
              </button>
            </dd>
          </div>
        }

        <ng-content select="[summary-operational-details]" />
      }
    </dl>
  </div>
</section>

@if (isAdopterUnknown()) {
  <div class="petszel-warning-alert">
    <div class="petszel-warning-alert__icon">
      <lib-icon iconName="utilityWarningIcon"></lib-icon>
    </div>
    <div class="petszel-warning-alert__content">
      This adopter did not opt in to post-adoption support at checkout. Please
      refer to the knowledge base article
      <a
        href="https://support.petszel.com/en-us/sheltersupport/how-do-i-update?hs_preview=PcbZXWLs-188752473110"
        target="_blank">here</a
      >
      to update this record.
    </div>
  </div>
}

<div class="custom-divider" aria-hidden="true"></div>
